// Tarefa com pausas (0.67, texto 05 A1–A5): trechos de atividade e pausa, emoji e aviso das pausas.
// Os horários das pausas saem dos mesmos dados que o aviso grande da tela (PauseOverlay) usa: Pause.at e Pause.min,
// e os trechos de atividade de blocos() (lib/time), o mesmo resumo "Atividade: 09:00 às 10:00 / ..." da ficha.
import type { Ajustes, ModoAviso, Task } from '../store/types';
import { getDoc } from '../store/store';
import { blocos, toMin } from './time';
import { instante } from './fuso';
import { estadoAgora, type EstadoTrecho, type Trecho } from './temporizador';
import { modoDaAba } from './avisos';

/** Emojis da pausa (A3). O primeiro é o padrão. Só emojis do próprio celular. */
export const EMOJIS_PAUSA = ['☕', '⏸️', '😴', '😪', '🥱', '😌', '💤', '☕⏰'];

/** Emoji da pausa: o da tarefa, senão o de Ajustes > Pausas, senão ☕. */
export function emojiDaPausa(tk: Pick<Task, 'pausaEmoji'>, aj?: Ajustes): string {
  let padrao: string | undefined;
  try { padrao = (aj ?? getDoc('ajustes')).pausas?.emoji; } catch { padrao = undefined; }
  return tk.pausaEmoji || padrao || EMOJIS_PAUSA[0];
}

/** Como avisar no começo e no fim das pausas: o da tarefa, senão Ajustes > Pausas, senão o padrão (aba/app).
 *  escolhido = alguém mudou (tarefa, Pausas, aba ou app); sem escolha nenhuma, o app avisa como antes. */
export function modoDaPausa(tk: Pick<Task, 'pausaAviso' | 'area'>, aj?: Ajustes): { modo: ModoAviso; escolhido: boolean } {
  let a: Ajustes | undefined;
  try { a = aj ?? getDoc('ajustes'); } catch { a = undefined; }
  const m = tk.pausaAviso ?? a?.pausas?.aviso;
  if (m) return { modo: m, escolhido: true };
  return { modo: modoDaAba('planner'), escolhido: !!(a?.avisos?.modo || a?.avisosAba?.planner) };
}

/** Trechos da tarefa do começo ao fim: atividade, pausa, atividade... (para o mini temporizador e a barrinha). */
export function trechosDaTarefa(tk: Pick<Task, 'time' | 'duration' | 'pauses'>): Trecho[] {
  if (!tk.time) return [];
  const ini = toMin(tk.time), fim = ini + tk.duration;
  const out: Trecho[] = [];
  let cur = ini;
  for (const b of blocos(tk.time, tk.duration, tk.pauses)) {
    let de = toMin(b.de), ate = toMin(b.ate);
    while (de < cur) de += 1440;        // passou da meia-noite
    while (ate <= de) ate += 1440;
    if (de > cur) out.push({ tipo: 'pausa', min: de - cur });
    out.push({ tipo: 'ativ', min: ate - de });
    cur = ate;
  }
  if (cur < fim) out.push({ tipo: 'pausa', min: fim - cur });   // a tarefa termina durante uma pausa
  return out;
}

export type PausasAgora = { inicioMs: number; fimMs: number; trechos: Trecho[]; estado: EstadoTrecho };
/** Tarefa com pausas acontecendo agora (nesta data, entre o começo e o fim, não concluída)? */
export function pausasAgora(tk: Task, date: string, agoraMs = Date.now()): PausasAgora | null {
  if (!tk.time || !tk.pauses.length || tk.done.includes(date)) return null;
  const trechos = trechosDaTarefa(tk);
  if (!trechos.some(x => x.tipo === 'pausa')) return null;
  const inicioMs = instante(date, tk.time, tk.tz).getTime();
  const fimMs = inicioMs + tk.duration * 60000;
  if (agoraMs < inicioMs || agoraMs >= fimMs) return null;
  return { inicioMs, fimMs, trechos, estado: estadoAgora(inicioMs, trechos, agoraMs) };
}
